// 2026-02 Phase C4 · client-side runtime error beacon.
//
// Ships a compact JSON payload to /api/client-errors whenever the
// SPA throws.  Purpose is post-mortem visibility for the owner —
// see /api/admin/client-errors.
//
// Design goals:
//   1. Fire-and-forget.  A backend outage MUST NOT slow the app or
//      throw a second error while reporting the first.
//   2. `navigator.sendBeacon` preferred (works during page unload);
//      fall back to `fetch(..., { keepalive: true })` when the payload
//      shape doesn't play with sendBeacon's opaque `Blob` API.
//   3. In-memory dedupe + rate limit — if the app enters an error loop
//      we don't hammer the ingest with the same trace 100 times.
//   4. Never send: request/response bodies, cookies, form values, or
//      anything not passed to us explicitly.  The backend also
//      whitelists the fields it stores.
//
// Payload shape (matches _CLIENT_ERROR_ALLOWED_FIELDS on the server):
//   { message, stack, url, route, sw_version, app_build, user_agent,
//     kind, component_stack, context, ts }

const ENDPOINT = "/api/client-errors";
const MAX_QUEUE = 10;            // never store more than this many
const MAX_PER_WINDOW = 6;        // how many beacons per rolling minute
const WINDOW_MS = 60 * 1000;
const DEDUPE_MS = 5 * 1000;      // drop identical repeats within 5s
const STACK_CAP = 4000;          // matches server-side cap
const MSG_CAP = 500;

const _recentSignatures = new Map(); // signature → timestamp of last send
const _sendTimestamps = [];          // rolling window of send timestamps

function _capStr(s, n) {
  if (typeof s !== "string") return undefined;
  return s.length > n ? s.slice(0, n) : s;
}

function _sig(payload) {
  // Signature is (kind + first line of message + first stack frame).
  // Two errors with identical signatures within DEDUPE_MS are
  // considered the same event and only the first one is sent.
  const firstLine = (payload.message || "").split("\n", 1)[0] || "";
  const firstFrame = ((payload.stack || "").split("\n").find((l) => l.trim().startsWith("at ")) || "").trim();
  return `${payload.kind || "error"}::${firstLine}::${firstFrame}`;
}

function _prune(now) {
  while (_sendTimestamps.length && _sendTimestamps[0] < now - WINDOW_MS) {
    _sendTimestamps.shift();
  }
  for (const [k, t] of _recentSignatures) {
    if (t < now - DEDUPE_MS) _recentSignatures.delete(k);
  }
}

function _getBackendBase() {
  const env = typeof process !== "undefined" ? process.env.REACT_APP_BACKEND_URL : "";
  return (env || "").replace(/\/+$/, "");
}

function _send(payload) {
  const url = `${_getBackendBase()}${ENDPOINT}`;
  const body = JSON.stringify(payload);
  // Prefer sendBeacon — it survives the page-unload race, is
  // asynchronous by contract, and never awaits.  When it fails
  // (payload > 64 KB — we never send anything that large — or when
  // sendBeacon isn't available on the runtime) we fall back to
  // keepalive fetch.  Both paths swallow errors.
  try {
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(url, blob);
      if (ok) return;
    }
  } catch { /* fall through */ }
  try {
    fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      credentials: "include",
      keepalive: true,
    }).catch(() => { /* fire-and-forget */ });
  } catch { /* swallow — beacon must never throw */ }
}

/**
 * Public entry point.  Called by window.onerror, unhandledrejection,
 * ErrorBoundary.componentDidCatch, and any code that wants to log a
 * caught exception for later analysis.
 *
 * @param {object} opts
 * @param {string} opts.message
 * @param {string} [opts.stack]
 * @param {string} [opts.kind]  one of "error"|"unhandledrejection"|"boundary"|"manual"
 * @param {string} [opts.componentStack]  React component stack (boundary)
 * @param {string} [opts.context]  free-form label ("editor drag", ...)
 */
export function reportClientError(opts) {
  try {
    if (!opts || (!opts.message && !opts.stack)) return;
    const now = Date.now();
    _prune(now);
    if (_sendTimestamps.length >= MAX_PER_WINDOW) return; // over the ceiling
    const payload = {
      kind: opts.kind || "error",
      message: _capStr(opts.message, MSG_CAP),
      stack: _capStr(opts.stack, STACK_CAP),
      component_stack: _capStr(opts.componentStack, 2000),
      context: _capStr(opts.context, 1000),
      url: typeof window !== "undefined" ? _capStr(window.location.href, 1000) : undefined,
      route: typeof window !== "undefined" ? _capStr(window.location.pathname, 250) : undefined,
      sw_version: (typeof window !== "undefined" && window.__GC_SW_VERSION) ? String(window.__GC_SW_VERSION).slice(0, 60) : undefined,
      user_agent: (typeof navigator !== "undefined" && navigator.userAgent) ? _capStr(navigator.userAgent, 500) : undefined,
      ts: now,
    };
    const signature = _sig(payload);
    const lastSent = _recentSignatures.get(signature);
    if (lastSent && now - lastSent < DEDUPE_MS) return;
    _recentSignatures.set(signature, now);
    _sendTimestamps.push(now);
    if (_recentSignatures.size > MAX_QUEUE) {
      // Should never happen thanks to the prune above, but keep the
      // map from growing unbounded if timers get frozen.
      const oldest = Array.from(_recentSignatures.entries()).sort((a, b) => a[1] - b[1])[0];
      if (oldest) _recentSignatures.delete(oldest[0]);
    }
    _send(payload);
  } catch { /* never throw from the reporter */ }
}

/**
 * Wire up the browser globals.  Idempotent — safe to call multiple
 * times.  Called once from src/index.js at bootstrap.
 */
export function installGlobalErrorHandlers() {
  if (typeof window === "undefined" || window.__GC_CLIENT_ERRORS_INSTALLED) return;
  window.__GC_CLIENT_ERRORS_INSTALLED = true;

  // Synchronous errors.
  window.addEventListener("error", (event) => {
    // Filter out CORS-tainted "Script error." with no useful info.
    const msg = event?.message || (event?.error && event.error.message) || "";
    if (!msg && !event?.error) return;
    reportClientError({
      kind: "error",
      message: msg || String(event?.error || "unknown error"),
      stack: event?.error && event.error.stack,
    });
  });

  // Promise rejections.
  window.addEventListener("unhandledrejection", (event) => {
    const reason = event?.reason;
    let msg, stack;
    if (reason instanceof Error) {
      msg = reason.message;
      stack = reason.stack;
    } else {
      msg = typeof reason === "string" ? reason : JSON.stringify(reason).slice(0, 500);
    }
    reportClientError({ kind: "unhandledrejection", message: msg, stack });
  });
}
